// services/endowment.service.js
import CryptoJS from 'crypto-js';
import { ensureS3SafeFileName } from './api.service';
import { CONTENT_ENDPOINTS, STREAM_ENDPOINTS, DELETE_ENDPOINTS, INHERITANCE_ENDPOINTS } from '../utils/constants';

const ENCRYPTION_KEY = 'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2';

/**
 * Clear cache for inheritance data
 */
export const clearCache = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.includes('cache-bucket-inheritance')) {
      localStorage.removeItem(key);
    }
  });
  Object.keys(sessionStorage).forEach(key => {
    if (key.includes('cache-bucket-inheritance')) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Get the list of inheritances
 * @param {string} token - Authentication token
 * @param {boolean} forceClearCache - Whether to clear cache
 * @returns {Promise<Array>} List of inheritances
 */
export const getInheritanceList = async (token, forceClearCache = false) => {
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  if (forceClearCache) {
    clearCache();
  }
  
  try {
    const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ bucket: 'inheritance' }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result || !result.filesMetadata) {
      return [];
    }
    
    // Transform data to a more usable format
    return result.filesMetadata.map((file, index) => ({
      id: index + 1,
      name: file.Key,
      size: file.Size,
      lastModified: file.LastModified
    }));
  } catch (error) {
    console.error('Error fetching inheritance list:', error);
    throw error;
  }
};

/**
 * Get inheritance details - Fixed to match static JS implementation
 * @param {string} inheritanceName - Inheritance name
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Inheritance details
 */
export const getInheritance = async (inheritanceName, token) => {
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(STREAM_ENDPOINTS.FILE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bucket: 'inheritance',
        fileName: inheritanceName,
      }),
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const text = await response.text();
    return parseInheritanceContent(text);
  } catch (error) {
    console.error('Error fetching inheritance:', error);
    throw error;
  }
};

/**
 * Parse inheritance content from text format - Fixed to match static JS
 * @param {string} content - Inheritance content
 * @returns {Object} Parsed inheritance
 */
const parseInheritanceContent = (content) => {
  const lines = content.split('\n');
  const data = {};
  
  lines.forEach(line => {
    const separatorIndex = line.indexOf(': ');
    if (separatorIndex !== -1) {
      const key = line.substring(0, separatorIndex).trim();
      const value = line.substring(separatorIndex + 2).trim();
      data[key] = value;
    }
  });
  
  // Decrypt selected items if available
  if (data['Selected Items']) {
    try {
      const decryptedItems = CryptoJS.AES.decrypt(
        data['Selected Items'],
        ENCRYPTION_KEY
      ).toString(CryptoJS.enc.Utf8);
      
      // Safely parse JSON with error handling
      try {
        if (decryptedItems && decryptedItems.trim()) {
          data.selectedItems = JSON.parse(decryptedItems);
        } else {
          console.warn('Decrypted items string is empty');
          data.selectedItems = { 
            files: [], 
            passwords: [], 
            banking: [], 
            blockchain: [] 
          };
        }
      } catch (parseError) {
        console.error('Error parsing selected items JSON:', parseError);
        data.selectedItems = { 
          files: [], 
          passwords: [], 
          banking: [], 
          blockchain: [] 
        };
      }
    } catch (decryptError) {
      console.error('Error decrypting selected items:', decryptError);
      data.selectedItems = { 
        files: [], 
        passwords: [], 
        banking: [], 
        blockchain: [] 
      };
    }
  } else {
    data.selectedItems = { 
      files: [], 
      passwords: [], 
      banking: [], 
      blockchain: [] 
    };
  }
  
  return {
    alias: data['Alias'] || '',
    account: data['Inheritance Account'] || '',
    duration: data['Duration'] || '',
    selectedItems: data.selectedItems
  };
};

/**
 * Upload a new inheritance - Fixed to match static JS encryption
 * @param {Object} data - Inheritance data
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the upload operation
 */
export const uploadInheritance = async (data, token) => {
  const { alias, account, selectedItems, duration, Email } = data;

  const safeAlias = ensureS3SafeFileName(alias);
  
  if (!safeAlias || !account || !selectedItems || !duration || !Email) {
    throw new Error('Missing required fields');
  }
  
  try {
    // Encrypt selectedItems the same way as static JS
    const selectedItemsCiphertext = CryptoJS.AES.encrypt(
      JSON.stringify(selectedItems), 
      ENCRYPTION_KEY
    ).toString();

    const payload = {
      alias: safeAlias,
      account: account,
      selectedItems: selectedItemsCiphertext,
      duration: duration,
      Email: Email
    };

    const response = await fetch(INHERITANCE_ENDPOINTS.UPLOAD, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error uploading inheritance:', error);
    throw error;
  }
};

/**
 * Edit an existing inheritance - Fixed to match static JS encryption
 * @param {Object} data - Inheritance data
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the edit operation
 */
export const editInheritance = async (data, token) => {
  const { 
    oldAlias, 
    newAlias, 
    oldRecipientEmail, 
    newRecipientEmail, 
    selectedItems, 
    duration, 
    Email 
  } = data;

  const safeNewAlias = ensureS3SafeFileName(newAlias);
  
  if (!oldAlias || !safeNewAlias || !oldRecipientEmail || !newRecipientEmail || 
      !selectedItems || !duration || !Email) {
    throw new Error('Missing required fields');
  }
  
  try {
    // Encrypt selectedItems the same way as static JS
    const selectedItemsCiphertext = CryptoJS.AES.encrypt(
      JSON.stringify(selectedItems), 
      ENCRYPTION_KEY
    ).toString();

    const payload = {
      oldAlias: oldAlias,
      newAlias: safeNewAlias,
      oldRecipientEmail: oldRecipientEmail,
      newRecipientEmail: newRecipientEmail,
      selectedItems: selectedItemsCiphertext,
      duration: duration,
      Email: Email
    };

    const response = await fetch(INHERITANCE_ENDPOINTS.EDIT, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error editing inheritance:', error);
    throw error;
  }
};

/**
 * Delete an inheritance (both file and database record)
 * @param {string} inheritanceName - Inheritance name
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
export const deleteInheritance = async (inheritanceName, token) => {
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    // First, get the inheritance data to extract recipient email
    
    let inheritanceData = null;
    try {
      inheritanceData = await getInheritance(inheritanceName, token);
      
    } catch (error) {
      console.warn(`⚠️ Could not retrieve inheritance data before deletion:`, error.message);
      // Continue with file deletion even if we can't get the data
    }
    
    // Get user email for database deletion
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }
    
    // Step 1: Delete from database if we have the inheritance data
    if (inheritanceData && inheritanceData.account) {
      try {
        
        const dbDeleteResponse = await fetch(DELETE_ENDPOINTS.INHERITANCE_DB, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            creatorEmail: userEmail,
            recipientEmail: inheritanceData.account
          }),
        });
        
        if (!dbDeleteResponse.ok) {
          console.warn(`⚠️ Database deletion failed with status: ${dbDeleteResponse.status}`);
          // Don't throw error here - continue with file deletion
        }
      } catch (dbError) {
        console.warn(`⚠️ Error deleting from database:`, dbError.message);
        // Continue with file deletion even if database deletion fails
      }
    } else {
      console.warn(`⚠️ Skipping database deletion - no recipient email found`);
    }
    
    // Step 2: Delete the file from S3
    
    const fileDeleteResponse = await fetch(DELETE_ENDPOINTS.FILE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bucket: 'inheritance',
        fileName: inheritanceName,
      }),
    });
    
    if (!fileDeleteResponse.ok) {
      throw new Error(`Failed to delete file: HTTP error! status: ${fileDeleteResponse.status}`);
    }
    
    
    // Clear cache after successful deletion
    clearCache();
    
    return true;
    
  } catch (error) {
    console.error('❌ Error deleting inheritance:', error);
    throw error;
  }
};

/**
 * Check if recipient email exists in the system
 * @param {Object} data - The data object containing the email to check
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the check
 */
export const checkRecipientEmail = async (data, token) => {
  const { email, creatorEmail } = data;
  
  if (!email || !creatorEmail) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(
      `${INHERITANCE_ENDPOINTS.CHECK_RECIPIENT_EMAIL}?email=${encodeURIComponent(email)}&creatorEmail=${encodeURIComponent(creatorEmail)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking recipient email:', error);
    throw error;
  }
};

/**
 * Get inheritance status
 * @param {Object} data - The data object containing creator and recipient emails
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Status of the inheritance
 */
export const getInheritanceStatus = async (data, token) => {
  const { creatorEmail, recipientEmail } = data;
  
  if (!creatorEmail || !recipientEmail) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(INHERITANCE_ENDPOINTS.STATUS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        creatorEmail,
        recipientEmail
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting inheritance status:', error);
    throw error;
  }
};

/**
 * Get duration for inheritance
 * @param {Object} data - The data object containing creator and recipient emails
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Duration details
 */
export const getDuration = async (data, token) => {
  const { creatorEmail, recipientEmail } = data;
  
  if (!creatorEmail) {
    throw new Error('Missing creator email');
  }
  
  try {
    const response = await fetch(INHERITANCE_ENDPOINTS.DURATION, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        creatorEmail,
        recipientEmail: recipientEmail || ''
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting duration:', error);
    throw error;
  }
};

/**
 * Resend a rejected inheritance with enhanced error handling
 * @param {Object} data - Inheritance data
 * @param {string} data.inheritanceId - The inheritance ID
 * @param {string} data.creatorEmail - Creator's email
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the resend operation
 */
export const resendInheritance = async (data, token) => {
  const { inheritanceId, creatorEmail } = data;
  
  if (!inheritanceId || !creatorEmail) {
    throw new Error('Missing required fields: inheritanceId and creatorEmail are required');
  }
  
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(INHERITANCE_ENDPOINTS.RESEND, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        inheritanceId,
        creatorEmail
      }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the status
        console.warn('Could not parse error response:', parseError);
      }
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error resending inheritance:', error);
    
    // Provide more specific error messages based on common scenarios
    if (error.message.includes('404')) {
      throw new Error('Inheritance not found or has already been processed');
    } else if (error.message.includes('403')) {
      throw new Error('You do not have permission to resend this inheritance');
    } else if (error.message.includes('400')) {
      throw new Error('Invalid request - the inheritance may not be in a resendable state');
    } else if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      throw new Error('Network error - please check your connection and try again');
    }
    
    throw error;
  }
};

/**
 * Get the current (most recent) inheritance for the logged-in user
 * This ensures we always work with the newest inheritance file
 * @param {string} token - Authentication token
 * @param {string} userEmail - Current user's email
 * @returns {Promise<Object|null>} Current inheritance data or null if none exists
 */
export const getCurrentInheritance = async (token, userEmail) => {
  if (!token || !userEmail) {
    throw new Error('Token and user email are required');
  }
  
  try {
    
    // Get all inheritance files
    const inheritances = await getInheritanceList(token, false);
    
    if (inheritances.length === 0) {
      return null;
    }
    
    
    let validInheritances = [];
    
    // Check each inheritance to find ones that belong to this user
    for (const inheritanceFile of inheritances) {
      try {
        // Parse the inheritance file
        const inheritanceData = await getInheritance(inheritanceFile.name, token);
        
        // Check if this inheritance exists in database and belongs to current user
        const status = await getInheritanceStatus({
          creatorEmail: userEmail,
          recipientEmail: inheritanceData.account
        }, token);
        
        if (status?.exists) {
          validInheritances.push({
            file: inheritanceFile,
            data: inheritanceData,
            status: status,
            lastModified: new Date(inheritanceFile.LastModified)
          });
        }
      } catch (error) {
        console.warn(`⚠️  Skipping invalid inheritance file: ${inheritanceFile.name}`, error.message);
        continue;
      }
    }
    
    if (validInheritances.length === 0) {
      return null;
    }
    
    // Sort by last modified date (newest first) and return the most recent
    validInheritances.sort((a, b) => b.lastModified - a.lastModified);
    const currentInheritance = validInheritances[0];
    
    
    if (validInheritances.length > 1) {
      console.warn(`⚠️  Multiple valid inheritances found. Using most recent: ${currentInheritance.file.name}`);
      console.warn(`Other files:`, validInheritances.slice(1).map(i => `${i.file.name} (${i.lastModified})`));
    }
    
    // Return the processed inheritance data
    const safeSelectedItems = {
      files: Array.isArray(currentInheritance.data.selectedItems?.files) ? currentInheritance.data.selectedItems.files : [],
      passwords: Array.isArray(currentInheritance.data.selectedItems?.passwords) ? currentInheritance.data.selectedItems.passwords : [],
      banking: Array.isArray(currentInheritance.data.selectedItems?.banking) ? currentInheritance.data.selectedItems.banking : [],
      blockchain: Array.isArray(currentInheritance.data.selectedItems?.blockchain) ? currentInheritance.data.selectedItems.blockchain : []
    };
    
    return {
      ...currentInheritance.data,
      originalName: currentInheritance.file.name,
      status: currentInheritance.status?.exists ? currentInheritance.status.status : null,
      selectedItems: safeSelectedItems,
      lastModified: currentInheritance.lastModified
    };
    
  } catch (error) {
    console.error('❌ Error getting current inheritance:', error);
    throw error;
  }
};

/**
 * Add a file to inheritance
 * @param {string} fileName - File name or path
 * @param {string} inheritanceName - Inheritance name
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
export const addFileToInheritance = async (fileName, inheritanceName, token) => {
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }
    
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      throw new Error('No active endowments found for current user');
    }
    
    
    // Check if the file is already in the inheritance
    if (currentInheritance.selectedItems.files.includes(fileName)) {
      throw new Error('File is already added to this endowment');
    }
    
    // Add the file to the selected items
    const updatedItems = {
      ...currentInheritance.selectedItems,
      files: [...currentInheritance.selectedItems.files, fileName]
    };
    
    // Use uploadInheritance to update the inheritance with the new file
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);
    
    // Clear cache to ensure fresh data
    clearCache();
    
    return true;
  } catch (error) {
    console.error('❌ Error adding file to inheritance:', error);
    throw error;
  }
};


/**
 * Update inheritance after file deletion
 * @param {string} deletedFileName - Deleted file name
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
export const updateInheritancesAfterFileDeletion = async (deletedFileName, token) => {
  try {
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }
    
    
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      return true;
    }
    
    const fileIndex = currentInheritance.selectedItems.files.indexOf(deletedFileName);
    
    if (fileIndex === -1) {
      return true;
    }
    
    
    // Remove the file from the inheritance
    const updatedItems = {
      ...currentInheritance.selectedItems,
      files: currentInheritance.selectedItems.files.filter(f => f !== deletedFileName)
    };
    
    // Use uploadInheritance to update the inheritance without the deleted file
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);
    
    // Clear cache after update
    clearCache();
    
    return true;
  } catch (error) {
    console.error('❌ Error updating inheritance after file deletion:', error);
    throw error;
  }
};

/**
 * Remove TXT extension from inheritance name
 * @param {string} name - Inheritance name
 * @returns {string} Name without extension
 */
export const removeTxtExtension = (name) => {
  if (!name) return '';
  const nameStr = String(name);
  if (nameStr.endsWith && nameStr.endsWith('.txt')) {
    return nameStr.slice(0, -4);
  }
  return nameStr;
};

/**
 * Ensure inheritance name has TXT extension
 * @param {string} name - Inheritance name
 * @returns {string} Name with extension
 */
export const ensureTxtExtension = (name) => {
  if (!name) return '';
  const nameStr = String(name);
  if (nameStr.endsWith && nameStr.endsWith('.txt')) {
    return nameStr;
  }
  return nameStr + '.txt';
};

/**
 * Check inheritance availability
 * @param {string} inheritanceId - Inheritance ID
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Availability status
 */
export const checkInheritanceAvailability = async (inheritanceId, token) => {
  if (!inheritanceId) {
    throw new Error('Missing inheritance ID');
  }
  
  try {
    const response = await fetch(INHERITANCE_ENDPOINTS.CHECK_AVAILABILITY, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ inheritanceId })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking inheritance availability:', error);
    throw error;
  }
};